import { BaseService } from '../../infra/base.service'
import type { CreateInvoiceDto } from './dto/create-invoice.dto'
import type { Purchase } from './purchase.entity'
import type { PurchasesRepository } from './purchases.respository'
import type { AssignNewCardDto } from '../customer-cards/dto/assign-new-card.dto'
import type { ChangePurchaseDueDateInputDto, ChangePurchaseDueDateOutputDto, ChangePurchaseValueInputDto } from './dto/change-purchase-due-date.dto'
import type { RevokeScheduledCancelOutputDto, ScheduleCancelInputDto, ScheduleCancelOutputDto } from './dto/schedule-cancel.dto'
import type { ChangeGatewayInputDto, ChangeGatewayOutputDto } from './dto/change-gateway.dto'
import type { ChangeMethodInputDto, ChangeMethodOutputDto } from './dto/change-method.dto'
import type { PayerDto, UpdatePayerDto } from './dto/payer.dto'
import type { ApplyPriceAdjustmentDto, PriceAdjustmentConfigDto, PriceAdjustmentPreviewDto, RevertPriceAdjustmentPreviewDto, PriceIndex } from './dto/price-adjustment.dto'

export class PurchasesService extends BaseService<Purchase> {
    constructor(private rep: PurchasesRepository) {
        super(rep)
    }

    async createInvoice(dto: CreateInvoiceDto) {
        return await this.rep.createInvoice(dto)
    }

    async updateCard(purchaseId: string, dto: AssignNewCardDto) {
        return await this.rep.updateCard(purchaseId, dto)
    }

    async getOnePublic(purchaseId: string, charges: boolean = false): Promise<Purchase> {
        return await this.rep.getOnePublic(purchaseId, charges)
    }

    async billIntegration(purchaseId: string, dto: { billIntegration: boolean }) {
        return await this.rep.billIntegration(purchaseId, dto)
    }

    async changeValue(purchaseId: string, dto: ChangePurchaseValueInputDto): Promise<{ success: boolean }> {
        return await this.rep.changeValue(purchaseId, dto)
    }

    async changeDueDate(purchaseId: string, dto: ChangePurchaseDueDateInputDto): Promise<ChangePurchaseDueDateOutputDto> {
        return await this.rep.changeDueDate(purchaseId, dto)
    }

    // agenda o encerramento da assinatura para uma data futura. com simulation=true apenas
    // devolve o que aconteceria, sem gravar nada.
    async changeGateway(purchaseId: string, dto: ChangeGatewayInputDto): Promise<ChangeGatewayOutputDto> {
        return await this.rep.changeGateway(purchaseId, dto)
    }

    async getPayer(purchaseId: string): Promise<PayerDto> {
        return await this.rep.getPayer(purchaseId)
    }

    async updatePayer(purchaseId: string, dto: UpdatePayerDto): Promise<PayerDto> {
        return await this.rep.updatePayer(purchaseId, dto)
    }

    async changeMethod(purchaseId: string, dto: ChangeMethodInputDto): Promise<ChangeMethodOutputDto> {
        return await this.rep.changeMethod(purchaseId, dto)
    }

    async scheduleCancel(purchaseId: string, dto: ScheduleCancelInputDto): Promise<ScheduleCancelOutputDto> {
        return await this.rep.scheduleCancel(purchaseId, dto)
    }

    async revokeScheduledCancel(purchaseId: string): Promise<RevokeScheduledCancelOutputDto> {
        return await this.rep.revokeScheduledCancel(purchaseId)
    }

    // ##### reajuste de preço por índice contratual #####

    async setPriceAdjustmentConfig(purchaseId: string, dto: PriceAdjustmentConfigDto) {
        return await this.rep.setPriceAdjustmentConfig(purchaseId, dto)
    }

    // o que aconteceria se reajustasse agora — mesma rotina que a aplicação usa, para a tela e o
    // sistema não poderem divergir
    async previewPriceAdjustment(purchaseId: string, index?: PriceIndex): Promise<PriceAdjustmentPreviewDto> {
        return await this.rep.previewPriceAdjustment(purchaseId, index)
    }

    async applyPriceAdjustment(purchaseId: string, dto: ApplyPriceAdjustmentDto): Promise<PriceAdjustmentPreviewDto> {
        return await this.rep.applyPriceAdjustment(purchaseId, dto)
    }

    async previewRevertPriceAdjustment(purchaseId: string): Promise<RevertPriceAdjustmentPreviewDto> {
        return await this.rep.previewRevertPriceAdjustment(purchaseId)
    }

    async revertPriceAdjustment(purchaseId: string): Promise<RevertPriceAdjustmentPreviewDto> {
        return await this.rep.revertPriceAdjustment(purchaseId)
    }
}
